import { api } from '../api/client';
import { useT } from '../i18n';

export interface DocumentSummary {
  id: number;
  source_file_id: number;
  filename: string;
  original_filename: string | null;
  measure_id: number | null;
  attribution_method: string | null;
  attribution_confidence: 'high' | 'medium' | 'low' | 'none' | null;
  page_count: number | null;
  size_bytes: number | null;
  doc_type: string | null;
  summary: string | null;
  detected_markers: Record<string, unknown> | null;
  uploaded_at: string | null;
  ingest_status: string;
  ocr_snippet: string | null;
}

interface Props {
  doc: DocumentSummary;
  onOpen: (doc: DocumentSummary) => void;
}

function formatSize(bytes: number | null): string {
  if (!bytes) return '—';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function formatDate(iso: string | null): string {
  if (!iso) return '—';
  return iso.slice(0, 10);
}

/** `t` is passed in from the component (plain helper, not a hook). */
function confidenceBadge(t: (key: string) => string, c: DocumentSummary['attribution_confidence']) {
  if (c === 'high') return { label: t('doc.confidence.high'), cls: 'badge-ok' };
  if (c === 'medium') return { label: t('doc.confidence.medium'), cls: 'badge-warn' };
  if (c === 'low' || c === 'none' || c === null) return { label: t('doc.confidence.none'), cls: 'badge-neutral' };
  return { label: String(c), cls: 'badge-neutral' };
}

/** `t` is passed in from the component (plain helper, not a hook). */
function statusBadge(t: (key: string) => string, status: string) {
  if (status === 'done') return null;
  if (status === 'failed') return { label: t('doc.status.ocrFailed'), cls: 'badge-err' };
  return { label: t('doc.status.ocrProcessing'), cls: 'badge-warn' };
}

export function DocumentCard({ doc, onOpen }: Props) {
  const { t } = useT();
  const conf = confidenceBadge(t, doc.attribution_confidence);
  const status = statusBadge(t, doc.ingest_status);
  const displayName = doc.original_filename || doc.filename;
  const thumbUrl = doc.ingest_status === 'done' ? api.documentPageImageUrl(doc.id, 1) : null;

  return (
    <div
      className="document-card"
      style={{
        display: 'flex',
        gap: 'var(--s-12)',
        padding: 'var(--s-12)',
        border: '1px solid var(--line)',
        borderRadius: 'var(--r1)',
        background: 'var(--surface)',
        cursor: doc.ingest_status === 'done' ? 'pointer' : 'default',
      }}
      onClick={() => doc.ingest_status === 'done' && onOpen(doc)}
    >
      {thumbUrl ? (
        <img
          src={thumbUrl}
          alt={t('doc.previewAlt', { name: displayName })}
          style={{
            width: 72,
            height: 96,
            objectFit: 'cover',
            objectPosition: 'top center',
            border: '1px solid var(--line)',
            background: 'var(--ink-9)',
            flexShrink: 0,
          }}
        />
      ) : (
        <div
          style={{
            width: 72,
            height: 96,
            background: 'var(--ink-8)',
            border: '1px solid var(--line)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'var(--ink-3)',
            fontSize: 'var(--t-10)',
            flexShrink: 0,
          }}
        >
          PDF
        </div>
      )}

      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            fontSize: 'var(--t-13)',
            fontWeight: 600,
            color: 'var(--ink-1)',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
          title={displayName}
        >
          {displayName}
        </div>

        <div
          style={{
            display: 'flex',
            gap: 'var(--s-8)',
            margin: 'var(--s-4) 0',
            flexWrap: 'wrap',
          }}
        >
          <span className={`badge ${conf.cls}`} style={{ fontSize: 'var(--t-10)' }}>
            {conf.label}
          </span>
          {status && (
            <span className={`badge ${status.cls}`} style={{ fontSize: 'var(--t-10)' }}>
              {status.label}
            </span>
          )}
          {doc.doc_type && (
            <span
              style={{
                fontSize: 'var(--t-10)',
                color: 'var(--ink-3)',
                fontStyle: 'italic',
              }}
            >
              {doc.doc_type}
            </span>
          )}
        </div>

        <div
          style={{
            fontSize: 'var(--t-11)',
            color: 'var(--ink-3)',
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
          }}
        >
          {doc.summary || doc.ocr_snippet || '—'}
        </div>

        <div
          style={{
            marginTop: 'var(--s-4)',
            fontSize: 'var(--t-10)',
            color: 'var(--ink-4)',
            display: 'flex',
            gap: 'var(--s-8)',
          }}
        >
          <span>{formatDate(doc.uploaded_at)}</span>
          <span>·</span>
          <span>
            {doc.page_count ?? '—'} {doc.page_count === 1 ? t('doc.page') : t('doc.pages')}
          </span>
          <span>·</span>
          <span>{formatSize(doc.size_bytes)}</span>
          <span style={{ marginLeft: 'auto' }}>
            <a
              href={api.documentBlobUrl(doc.id)}
              onClick={(e) => e.stopPropagation()}
              download={displayName}
              style={{ color: 'var(--ink-2)', textDecoration: 'underline' }}
            >
              {t('doc.download')}
            </a>
          </span>
        </div>
      </div>
    </div>
  );
}
